// src/contexts/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useRef,
} from "react";
import supabase from "../lib/supabaseClient";
import {
  Session,
  SupabaseClient,
  AuthChangeEvent,
  User,
} from "@supabase/supabase-js";
import { OAuthService } from "../services/oauthService";
import { OAuthProviderName } from "../config/oauthConfig";
import { apiService } from "../services/apiService";
import { API_URL, FRONTEND_URL } from "env-config";
import { isVCSubdomain, getBaseUrl } from "../utils/subdomainDetection";
import { identifyHotjarUser } from "../services/hotjar";

// Enhanced auth context interface
type AuthContextType = {
  supabase: SupabaseClient;
  session: Session | null;
  user: User | null;
  userRole: string | null; // 'user', 'vc', or 'admin'
  loading: boolean;
  error: string | null;
  confirmationEmailSent: boolean;
  isAuthenticated: boolean;
  isOnboarded: boolean | null; // null means we haven't checked yet
  onboardingLoading: boolean;
  // Email OTP flows
  requestEmailLoginOtp: (
    email: string,
    metadata?: Record<string, any>
  ) => Promise<void>;
  requestEmailSignupOtp: (
    email: string,
    metadata?: Record<string, any>
  ) => Promise<void>;
  signInWithProvider: (provider: OAuthProviderName) => Promise<void>;
  signInWithGoogle: () => Promise<void>; // Simple Google sign-in for authentication
  signOut: () => Promise<void>;
  checkOnboardingStatus: () => Promise<void>;
  clearError: () => void;
  // Immediately mark onboarding complete in local state to prevent routing loops
  markOnboardingComplete: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmationEmailSent, setConfirmationEmailSent] =
    useState<boolean>(false);
  const [isOnboarded, setIsOnboarded] = useState<boolean | null>(null);
  const [onboardingLoading, setOnboardingLoading] = useState<boolean>(false);

  // Refs to prevent duplicate operations
  const onboardingCheckInProgress = useRef<boolean>(false);
  const initializationComplete = useRef<boolean>(false);

  // Helper to check onboarding status
  const checkOnboardingStatus = async () => {
    if (!session?.user || onboardingCheckInProgress.current) {
      console.log(
        "AuthContext: Skipping onboarding check - no user or already in progress"
      );
      if (!session?.user) {
        setIsOnboarded(null);
      }
      return;
    }

    console.log(
      "AuthContext: Starting onboarding status check for user:",
      session.user.id
    );
    onboardingCheckInProgress.current = true;
    setOnboardingLoading(true);

    try {
      console.log("AuthContext: Calling apiService.checkOnboardingStatus()");
      const status = await apiService.checkOnboardingStatus();

      if (status) {
        console.log("AuthContext: API returned status:", status);
        const isOnboarded = status.exists && status.onboardingComplete;
        console.log("AuthContext: Setting isOnboarded to:", isOnboarded);
        setIsOnboarded(isOnboarded);

        // Also set user role if available
        if (status.role) {
          console.log("AuthContext: Setting user role to:", status.role);
          setUserRole(status.role);
        }
      } else {
        console.log(
          "AuthContext: API check returned null, using fallback to user metadata"
        );

        // Fallback to user metadata when API is not available or returns 401
        const onboardingData = session.user.user_metadata?.onboarding;
        const hasFullName = !!session.user.user_metadata?.full_name;

        // More conservative approach: only consider onboarded if both conditions are met
        const isOnboardedFromMetadata = !!(
          onboardingData?.onboarding_complete && hasFullName
        );

        console.log("AuthContext: Metadata fallback analysis:", {
          onboardingData,
          hasFullName,
          isOnboardedFromMetadata,
          fullUserMetadata: session.user.user_metadata,
        });

        setIsOnboarded(isOnboardedFromMetadata);
      }
    } catch (error) {
      console.error("AuthContext: Error checking onboarding status:", error);

      // Fallback to user metadata on error
      const onboardingData = session.user.user_metadata?.onboarding;
      const hasFullName = !!session.user.user_metadata?.full_name;
      const isOnboardedFromMetadata = !!(
        onboardingData?.onboarding_complete && hasFullName
      );

      console.log("AuthContext: Error fallback analysis:", {
        onboardingData,
        hasFullName,
        isOnboardedFromMetadata,
        fullUserMetadata: session.user.user_metadata,
        errorMessage: error instanceof Error ? error.message : "Unknown error",
      });

      setIsOnboarded(isOnboardedFromMetadata);
    } finally {
      console.log(
        "AuthContext: Onboarding check complete, setting loading to false"
      );
      setOnboardingLoading(false);
      onboardingCheckInProgress.current = false;
    }
  };

  useEffect(() => {
    if (initializationComplete.current) {
      console.log("AuthContext: Skipping initialization - already complete");
      return;
    }

    console.log("AuthContext: Initializing auth state...");
    initializationComplete.current = true;

    // Fetch current session on mount
    supabase.auth
      .getSession()
      .then(({ data }) => {
        console.log(
          "AuthContext: Initial session loaded:",
          data.session ? "User logged in" : "No session"
        );
        setSession(data.session);
        setLoading(false);
      })
      .catch((error) => {
        console.error("AuthContext: Error loading initial session:", error);
        setLoading(false);
      });

    // Listen to auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      async (_event: AuthChangeEvent, newSession: Session | null) => {
        console.log(
          "AuthContext: Auth state changed:",
          _event,
          newSession ? "User logged in" : "User logged out"
        );

        // CRITICAL: Validate email on VC subdomain sign-in
        if (_event === "SIGNED_IN" && newSession?.user) {
          const isVC = isVCSubdomain();
          const pendingInviteEmail = localStorage.getItem(
            "vc_pending_invite_email"
          );
          const pendingInviteToken = localStorage.getItem(
            "vc_pending_invite_token"
          );

          console.log("🔐 Sign-in detected:", {
            isVC,
            userEmail: newSession.user.email,
            pendingInviteEmail,
          });

          // If on VC subdomain with pending invite, validate email immediately
          if (isVC && pendingInviteEmail) {
            const userEmail = newSession.user.email?.toLowerCase();
            const invitedEmail = pendingInviteEmail.toLowerCase();

            if (userEmail !== invitedEmail) {
              console.error("❌ EMAIL MISMATCH AT AUTH STATE CHANGE!");
              console.error("Expected:", invitedEmail);
              console.error("Got:", userEmail);

              // Immediately sign out - DON'T set session
              await supabase.auth.signOut();

              // Clear storage
              localStorage.removeItem("vc_pending_invite_token");
              localStorage.removeItem("vc_pending_invite_id");
              localStorage.removeItem("vc_pending_invite_email");

              // Redirect without alert - VCSignup will show inline error
              window.location.href = `/signup?invite=${
                pendingInviteToken || ""
              }&error=email_mismatch&expected=${encodeURIComponent(
                invitedEmail
              )}&got=${encodeURIComponent(userEmail || "unknown")}`;
              return; // Stop processing - don't set session
            }

            console.log("✅ Email validation passed at auth state level");
          }
        }

        setSession(newSession);
        setLoading(false);

        if (newSession) {
          setError(null);
          setConfirmationEmailSent(false);
          
          // Identify user in Hotjar for session replays
          if (newSession.user) {
            identifyHotjarUser(newSession.user.id, {
              email: newSession.user.email || '',
              created_at: newSession.user.created_at || '',
            });
          }
          
          // OAuth integration is handled by AppBootstrapper to prevent duplicates
        } else {
          // User signed out, reset onboarding state
          console.log(
            "AuthContext: User signed out, resetting onboarding state"
          );
          setIsOnboarded(null);
          onboardingCheckInProgress.current = false;
        }
      }
    );

    return () => {
      console.log("AuthContext: Cleaning up auth state listener");
      subscription.unsubscribe();
      initializationComplete.current = false;
    };
  }, []); // Empty dependency array to run only once

  // Check onboarding status when session changes (but prevent duplicates)
  useEffect(() => {
    console.log(
      "AuthContext: Session effect triggered, user ID:",
      session?.user?.id || "none"
    );

    if (session?.user) {
      console.log(
        "AuthContext: User session exists, checking onboarding status"
      );
      checkOnboardingStatus();
    } else {
      console.log("AuthContext: No user session, setting isOnboarded to null");
      setIsOnboarded(null);
      onboardingCheckInProgress.current = false;
    }
  }, [session?.user?.id]); // Only depend on user ID to prevent loops

  const user = session?.user ?? null;
  const isAuthenticated = !!session?.user;

  // Reset userRole when user logs out
  useEffect(() => {
    if (!session?.user) {
      setUserRole(null);
    }
  }, [session?.user]);

  // Email OTP: login for existing users
  const requestEmailLoginOtp = async (
    email: string,
    metadata?: Record<string, any>
  ) => {
    setError(null);
    setConfirmationEmailSent(false);

    // Include origin subdomain in metadata for redirect logic
    const isVC = isVCSubdomain();
    const enrichedMetadata = {
      ...metadata,
      originSubdomain: isVC ? 'vc' : 'main',
    };

    const { error: otpError } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${API_URL}/auth/v1/callback`,
        shouldCreateUser: false,
        data: enrichedMetadata,
      },
    });
    if (otpError) {
      setError(otpError.message);
    } else {
      setConfirmationEmailSent(true);
    }
    setLoading(false);
  };

  // Email OTP: signup for new users
  const requestEmailSignupOtp = async (
    email: string,
    metadata?: Record<string, any>
  ) => {
    setError(null);
    setConfirmationEmailSent(false);

    // Include origin subdomain in metadata for redirect logic
    const isVC = isVCSubdomain();
    const enrichedMetadata = {
      ...metadata,
      originSubdomain: isVC ? 'vc' : 'main',
    };

    const { error: otpError } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${API_URL}/auth/v1/callback`,
        shouldCreateUser: true,
        data: enrichedMetadata,
      },
    });
    if (otpError) {
      setError(otpError.message);
    } else {
      setConfirmationEmailSent(true);
    }
    setLoading(false);
  };

  // OAuth sign-in using centralized configuration (for integrations with extended scopes)
  const signInWithProvider = async (provider: OAuthProviderName) => {
    setLoading(true);
    setError(null);
    setConfirmationEmailSent(false);

    try {
      await OAuthService.signInWithProvider(provider);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "OAuth sign-in failed";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Simple Google sign-in for basic authentication (not integration)
  const signInWithGoogle = async () => {
    setLoading(true);
    setError(null);
    setConfirmationEmailSent(false);

    try {
      // Detect if we're on VC subdomain and set redirect accordingly
      const isVC = isVCSubdomain();

      // CRITICAL FIX: Use dynamic URL that matches current subdomain
      const currentBaseUrl = getBaseUrl(); // Gets correct subdomain dynamically
      const redirectUrl = isVC
        ? `${currentBaseUrl}/signup` // Will be https://venture.sendupdates.ai/signup or http://localhost:3006/signup
        : `${API_URL}/auth/v1/callback`; // Main app callback

      // Get the invited email for login_hint
      const invitedEmail = isVC
        ? localStorage.getItem("vc_pending_invite_email")
        : null;

      console.log("🔐 ===== OAUTH CONFIGURATION =====");
      console.log("Current URL:", window.location.href);
      console.log("Current hostname:", window.location.hostname);
      console.log("Current port:", window.location.port);
      console.log("Is VC Subdomain:", isVC);
      console.log("Current Base URL:", currentBaseUrl);
      console.log("Redirect URL:", redirectUrl);
      console.log("Login Hint (invited email):", invitedEmail);
      console.log("=================================");

      // Use Supabase's native OAuth
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            // Always show the Google account chooser
            prompt: "select_account",
            // Pre-fill with invited email (hint only, user can still change)
            ...(invitedEmail && { login_hint: invitedEmail }),
          },
        },
      });

      if (error) {
        console.error("Google OAuth error:", error);
        throw error;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Google sign-in failed";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setConfirmationEmailSent(false);
    setIsOnboarded(null);
    onboardingCheckInProgress.current = false;
    initializationComplete.current = false;
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  // Immediately mark onboarding complete in local state
  const markOnboardingComplete = () => {
    setIsOnboarded(true);
  };

  return (
    <AuthContext.Provider
      value={{
        supabase,
        session,
        user,
        userRole,
        loading,
        error,
        confirmationEmailSent,
        isAuthenticated,
        isOnboarded,
        onboardingLoading,
        requestEmailLoginOtp,
        requestEmailSignupOtp,
        signInWithProvider,
        signInWithGoogle,
        signOut,
        checkOnboardingStatus,
        clearError,
        markOnboardingComplete,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for accessing auth state
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
